import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import { useLinkConnection } from '@/hook/collection/useCollection';
import {
  ContentCopy,
  Done,
  VButton,
  VFlex,
  VSpacer,
  VStack,
  VTextButton,
  cn,
} from '@/package';

// TODO: 이름 수정
const NumberBox = ({ children }: PropsWithChildren) => {
  return (
    <VFlex
      className="size-6 rounded-sm bg-blue-300 text-body-xl text-white"
      justify="center"
      items="center"
    >
      {children}
    </VFlex>
  );
};

const Title = ({ index, title }: { index: number; title: string }) => {
  return (
    <VFlex gap={0.5}>
      <NumberBox>{index}</NumberBox>
      <div className="text-body-xl">{title}</div>
    </VFlex>
  );
};

const ContentLayout = ({ children }: PropsWithChildren) => {
  return <div className="pl-8">{children}</div>;
};

const UnorderedList = ({
  textList,
  variant = 'default',
}: {
  textList: string[];
  variant?: 'default' | 'important';
}) => {
  return (
    <VStack
      gap={0.25}
      className={cn('w-full text-body-lg', {
        'text-gray-500': variant === 'default',
        'text-error-500': variant === 'important',
      })}
    >
      {textList.map(text => (
        <VFlex key={text}>
          <span>•</span>
          <div>{text}</div>
        </VFlex>
      ))}
    </VStack>
  );
};

const Code = ({ json }: { json: object | string }) => {
  const formattedJson =
    typeof json === 'string' ? json : JSON.stringify(json, null, 2);

  return (
    <div className="relative max-h-[200px] min-h-[34px] overflow-y-auto rounded-md border border-gray-300 bg-gray-200 p-2 text-body-md leading-none text-gray-500">
      <pre className="translate-y-[1px] whitespace-pre-wrap">
        {formattedJson}
      </pre>

      <div className="absolute top-1 right-1">
        <VTextButton
          size="sm"
          onClick={() => {
            navigator.clipboard.writeText(formattedJson);
          }}
        >
          <VFlex items="center" gap={0.25}>
            <ContentCopy className="size-[14px]" />
            Copy
          </VFlex>
        </VTextButton>
      </div>
    </div>
  );
};

const ExternalStoragePageContent = () => {
  const { t } = useTranslation();
  const { mutate: linkConnection, isPending } = useLinkConnection();
  const contentList = [
    {
      title: t('storage.stepOne.stechados'),
      Content: () => {
        return (
          <UnorderedList
            textList={[
              t('storage.stepOneOne.mispaint'),
              t('storage.stepOneTwo.frigotherapy'),
            ]}
          />
        );
      },
    },
    {
      title: t('storage.stepTwo.typhula'),
      Content: () => {
        return (
          <VStack gap={0.25} className="w-full">
            <UnorderedList
              textList={[
                t('storage.stepTwoOne.communist'),
                t('storage.stepTwoTwo.clarice'),
              ]}
            />
            <Code
              json={{
                Version: '2012-10-17',
                Statement: [
                  {
                    Effect: 'Allow',
                    Principal: {
                      Federated: 'cognito-identity.amazonaws.com',
                    },
                    Action: 'sts:AssumeRoleWithWebIdentity',
                    Condition: {
                      StringEquals: {
                        'cognito-identity.amazonaws.com:aud':
                          'us-east-1:ede7c2e3-f213-491f-a4e4-57e6dd3a367a',
                      },
                      'ForAnyValue:StringLike': {
                        'cognito-identity.amazonaws.com:amr': 'authenticated',
                      },
                    },
                  },
                ],
              }}
            />

            <img
              src={`${import.meta.env.VITE_APP_STATIC_URL}/external_storage_trust_policy.png`}
              className="w-[717px]"
            />
          </VStack>
        );
      },
    },
    {
      title: t('storage.stepThree.goitcho'),
      Content: () => {
        return (
          <UnorderedList textList={[t('storage.stepThreeOne.punctureproof')]} />
        );
      },
    },
    {
      title: t('storage.stepFour.asarone'),
      Content: () => {
        return (
          <VStack gap={0.25}>
            <UnorderedList textList={[t('storage.stepFourOne.carriageway')]} />
            <Code json={'vuex-role'} />
            <img
              src={`${import.meta.env.VITE_APP_STATIC_URL}/external_storage_role.png`}
              className="w-[1075px]"
            />
          </VStack>
        );
      },
    },
    {
      title: t('storage.stepFive.angelship'),
      Content: () => {
        return (
          <VStack gap={0.25}>
            <UnorderedList
              textList={[
                t('storage.stepFiveOne.ambulancer'),
                t('storage.stepFiveTwo.annunciate'),
              ]}
            />
            <img
              src={`${import.meta.env.VITE_APP_STATIC_URL}/external_storage_inline_policy_1.png`}
              className="w-[868px]"
            />
            <VSpacer size={1.5} />
            <img
              src={`${import.meta.env.VITE_APP_STATIC_URL}/external_storage_inline_policy_2.png`}
              className="w-[886px]"
            />
          </VStack>
        );
      },
    },
    {
      title: t('storage.stepSix.acetometry'),
      Content: () => {
        return (
          <VStack gap={0.25}>
            <UnorderedList textList={[t('storage.stepSixOne.grasslike')]} />
            <Code
              json={{
                Version: '2012-10-17',
                Statement: [
                  {
                    Sid: 'VisualEditor0',
                    Effect: 'Allow',
                    Action: ['s3:ListAllMyBuckets', 's3:GetBucketLocation'],
                    Resource: 'arn:aws:s3:::*',
                  },
                  {
                    Sid: 'VisualEditor1',
                    Effect: 'Allow',
                    Action: ['s3:GetObject', 's3:ListBucket'],
                    Resource: ['arn:aws:s3:::Porori*'],
                  },
                ],
              }}
            />
            <img
              src={`${import.meta.env.VITE_APP_STATIC_URL}/external_storage_policy_json.png`}
              className="w-[826px]"
            />
            <UnorderedList
              textList={[
                t('storage.stepSixTwo.unwithheld'),
                t('storage.stepSixThree.hierarchize'),
              ]}
              variant="important"
            />
            <Code json={`"Resource": "arn:aws:s3:::{bucket}*"`} />
          </VStack>
        );
      },
    },
    {
      title: t('storage.stepSeven.spender'),
      Content: () => {
        return (
          <VStack gap={0.25}>
            <UnorderedList
              textList={[
                t('storage.stepSevenOne.duodenoenterostomy'),
                t('storage.stepSevenTwo.pauser'),
              ]}
            />
            <Code json={'vuex-role-policy'} />
            <img
              src={`${import.meta.env.VITE_APP_STATIC_URL}/external_storage_vuex-role-policy.png`}
              className="w-[883px]"
            />
          </VStack>
        );
      },
    },
    {
      title: t('storage.stepEight.acatamathesia'),
      Content: () => {
        return (
          <VStack gap={0.25}>
            <UnorderedList textList={[t('storage.stepEightOne.hydragogy')]} />
            <img
              src={`${import.meta.env.VITE_APP_STATIC_URL}/external_storage_link_1.png`}
              className="w-[722px]"
            />
            <UnorderedList
              textList={[
                t('storage.stepEightTwo.dietine'),
                t('storage.stepEightThree.submediant'),
              ]}
            />
            <Code
              json={{
                Version: '2008-10-17',
                Id: 'PolicyForCloudFrontPrivateContent',
                Statement: [
                  {
                    Sid: 'AllowCloudFrontServicePrincipalGet',
                    Effect: 'Allow',
                    Principal: {
                      Service: 'cloudfront.amazonaws.com',
                    },
                    Action: ['s3:GetObject'],
                    Resource: 'arn:aws:s3:::{OpenTargetBucket}/*',
                    Condition: {
                      StringLike: {
                        'AWS:SourceArn':
                          'arn:aws:cloudfront::206240260161:distribution/EU6CJ18G9EPPH',
                      },
                    },
                  },
                  {
                    Sid: 'AllowCloudFrontServicePrincipalList',
                    Effect: 'Allow',
                    Principal: {
                      Service: 'cloudfront.amazonaws.com',
                    },
                    Action: 's3:ListBucket',
                    Resource: 'arn:aws:s3:::{OpenTargetBucket}',
                    Condition: {
                      StringLike: {
                        'AWS:SourceArn':
                          'arn:aws:cloudfront::206240260161:distribution/EU6CJ18G9EPPH',
                      },
                    },
                  },
                ],
              }}
            />
            <img
              src={`${import.meta.env.VITE_APP_STATIC_URL}/external_storage_link_2.png`}
              className="w-[728px]"
            />

            <UnorderedList textList={[t('storage.stepEightFour.telegnosis')]} />

            <Code
              json={[
                {
                  AllowedHeaders: ['*'],
                  AllowedMethods: ['GET', 'PUT', 'DELETE', 'HEAD'],
                  AllowedOrigins: ['*'],
                  ExposeHeaders: [],
                },
              ]}
            />

            <img
              src={`${import.meta.env.VITE_APP_STATIC_URL}/external_storage_link_3.png`}
              className="w-[921px]"
            />
          </VStack>
        );
      },
    },
    {
      title: t('storage.stepNine.torpedolike'),
      Content: () => {
        return (
          <UnorderedList
            textList={[t('storage.stepNineOne.antistreptococcus')]}
          />
        );
      },
    },
  ];
  return (
    <VStack gap={1} className="h-full overflow-y-auto pt-6 pb-16">
      <div className="text-headline-md font-bold">
        {t('storage.linkTitle.stringcourse')}
      </div>

      <div className="text-body-xl">
        {t('storage.linkDescription.ultraradicalism')}
      </div>

      {contentList.map(({ title, Content }, index) => (
        <VStack key={title} gap={0.25}>
          <Title index={index + 1} title={title} />
          <ContentLayout>
            <Content />
          </ContentLayout>
        </VStack>
      ))}

      <VSpacer size={2} />

      <VFlex className="w-full" justify="center">
        <VButton onClick={() => linkConnection({})} disabled={isPending}>
          <VFlex gap={0.25}>
            <Done />
            {t('storage.verifyConnection.sackbut')}
          </VFlex>
        </VButton>
      </VFlex>
    </VStack>
  );
};

export default ExternalStoragePageContent;
